import { useEffect, useState } from "react";
import { Button, FileInput, Label, TextInput, Textarea } from "flowbite-react";
import { toast } from "react-toastify";
import CommonApi from "../../Api/CommonApi";
import PartnerOpportunitiesApi from "../../Api/PartnerOpportunitiesApi";
import { PartnerOpportunityRegionOption } from "../../Models/PartnerOpportunity";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";

const blankRegion: PartnerOpportunityRegionOption = {
  id: 0,
  name: "",
  description: "",
  imageUrl: "",
};

export default function PartnerOpportunityRegionsAdmin() {
  const { userModel } = useAppSelector((s) => s.user);
  const isSuperUser = userModel?.userLevelId === UserLevelNames.SuperAdmin;
  const [regions, setRegions] = useState<PartnerOpportunityRegionOption[]>([]);
  const [editing, setEditing] = useState<PartnerOpportunityRegionOption>(blankRegion);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const loadRegions = async () => {
    setIsLoading(true);
    try {
      const res = await PartnerOpportunitiesApi.getRegions();
      setRegions(res.data);
    } catch {
      toast.error("Unable to load partner opportunity regions.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadRegions();
  }, []);

  const editRegion = (region: PartnerOpportunityRegionOption) => {
    setImageFile(null);
    setEditing(region);
  };

  const newRegion = () => {
    setImageFile(null);
    setEditing(blankRegion);
  };

  const save = async () => {
    if (!editing.name.trim()) {
      toast.error("Region name is required.");
      return;
    }

    setIsSaving(true);
    try {
      let imageUrl = editing.imageUrl?.trim() || "";
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const uploadRes = await CommonApi.addImageFile(fd);
        imageUrl = (uploadRes.data as { url?: string })?.url ?? "";
      }

      const res = await PartnerOpportunitiesApi.upsertRegion({
        ...editing,
        name: editing.name.trim(),
        description: editing.description?.trim() || null,
        imageUrl: imageUrl || null,
      });

      if (!res.data.success) {
        toast.error(res.data.message || "Unable to save region.");
        return;
      }

      toast.success("Region saved.");
      newRegion();
      await loadRegions();
    } catch {
      toast.error("Unable to save region.");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRegion = async (region: PartnerOpportunityRegionOption) => {
    if (!window.confirm(`Delete region "${region.name}"?`)) return;

    const res = await PartnerOpportunitiesApi.deleteRegion(region.id);
    if (!res.data.success) {
      toast.error(res.data.message || "Unable to delete region.");
      return;
    }

    toast.success("Region deleted.");
    if (editing.id === region.id) newRegion();
    await loadRegions();
  };

  if (!isSuperUser) {
    return <p className="text-sm text-gray-600">You do not have access to manage partner opportunity regions.</p>;
  }

  return (
    <div className="w-full p-4 space-y-5">
      <div>
        <h1 className="text-4xl font-bold text-gray-900">Partner Opportunity Regions</h1>
        <p className="text-sm text-gray-600 mt-2">
          These regions drive the selectable list on partner opportunity forms.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="overflow-x-auto border rounded-xl bg-white">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-gray-200 text-gray-700">
                <th className="py-2 px-3 font-semibold">Name</th>
                <th className="py-2 px-3 font-semibold">Description</th>
                <th className="py-2 px-3 font-semibold">Image</th>
                <th className="py-2 px-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="py-3 px-3 text-gray-600" colSpan={4}>
                    Loading...
                  </td>
                </tr>
              ) : regions.length === 0 ? (
                <tr>
                  <td className="py-3 px-3 text-gray-600" colSpan={4}>
                    No regions have been added yet.
                  </td>
                </tr>
              ) : (
                regions.map((region) => (
                  <tr key={region.id} className="border-b border-gray-100 align-top">
                    <td className="py-3 px-3 font-semibold text-gray-900">{region.name}</td>
                    <td className="py-3 px-3 text-gray-700 max-w-lg">{region.description || "—"}</td>
                    <td className="py-3 px-3">
                      {region.imageUrl ? (
                        <img
                          src={region.imageUrl}
                          alt={region.name}
                          className="h-12 w-20 object-cover rounded border"
                        />
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right whitespace-nowrap">
                      <Button size="xs" color="light" className="mr-2" onClick={() => editRegion(region)}>
                        Edit
                      </Button>
                      <Button size="xs" color="failure" onClick={() => void deleteRegion(region)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="border rounded-xl bg-white p-4 space-y-3">
          <h2 className="text-xl font-bold text-gray-900">
            {editing.id > 0 ? "Edit Region" : "Add Region"}
          </h2>
          <div>
            <Label htmlFor="region-name" value="Name" />
            <TextInput
              id="region-name"
              value={editing.name}
              onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="region-description" value="Description" />
            <Textarea
              id="region-description"
              rows={4}
              value={editing.description ?? ""}
              onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="region-image-upload" value="Image Upload" />
            <FileInput
              id="region-image-upload"
              accept="image/*"
              helperText="Upload an image for this region."
              onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
            />
            {imageFile ? <p className="text-xs text-gray-500 mt-1">Selected: {imageFile.name}</p> : null}
          </div>
          <div>
            <Label htmlFor="region-image" value="Image URL" />
            <TextInput
              id="region-image"
              value={editing.imageUrl ?? ""}
              onChange={(e) => setEditing((s) => ({ ...s, imageUrl: e.target.value }))}
            />
            <p className="text-xs text-gray-500 mt-1">
              Uploading an image will replace this URL when you save.
            </p>
          </div>
          {editing.imageUrl ? (
            <img src={editing.imageUrl} alt="Region preview" className="h-28 w-full object-cover rounded border" />
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button className="buttonMain" onClick={() => void save()} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Region"}
            </Button>
            <Button color="light" onClick={newRegion} disabled={isSaving}>
              New Region
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
